import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SEO from "../../components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "../../components/Breadcrumbs";
import CatalogStats from "../../components/humanoid/CatalogStats";
import HumanoidRobotCard from "../../components/humanoid/HumanoidRobotCard";
import TopRatedRobots from "../../components/community/TopRatedRobots";
import RobotSuggestionModal from "../../components/community/RobotSuggestionModal";
import {
  buildCatalogCollectionSchema,
  getNumericPrice,
  humanoidCatalogStats,
  humanoidCountries,
  humanoidManufacturers,
  humanoidRobots,
} from "../../data/humanoidCatalog";

const PAGE_SIZE = 24;

function matchesSize(robot, size) {
  if (!size) return true;
  if (!Number.isFinite(robot.heightCm)) return false;
  if (size === "compact") return robot.heightCm < 120;
  if (size === "medium") return robot.heightCm >= 120 && robot.heightCm < 165;
  return robot.heightCm >= 165;
}

export default function HumanoidCatalogPage() {
  const [params, setParams] = useSearchParams();
  const [communityStats, setCommunityStats] = useState({});
  const [suggestOpen, setSuggestOpen] = useState(false);
  const query = params.get("q") || "";
  const manufacturer = params.get("manufacturer") || "";
  const country = params.get("country") || "";
  const availability = params.get("availability") || "";
  const price = params.get("price") || "";
  const category = params.get("category") || "";
  const size = params.get("size") || "";
  const year = params.get("year") || "";
  const sort = params.get("sort") || "featured";
  const requestedPage = Number(params.get("page") || 1);
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rows = humanoidRobots.filter((robot) => {
      const searchable = `${robot.name} ${robot.manufacturerName} ${robot.robotOriginCountry} ${robot.category} ${robot.summary}`.toLowerCase();
      if (needle && !searchable.includes(needle)) return false;
      if (manufacturer && robot.manufacturerId !== manufacturer) return false;
      if (country && robot.robotOriginCountryCode !== country) return false;
      if (availability && robot.availability !== availability) return false;
      if (price === "public" && !robot.publicPrice) return false;
      if (price === "quote" && robot.priceType !== "quote") return false;
      if (price === "unpublished" && !["not-commercialized"].includes(robot.priceType)) return false;
      if (price === "unverified" && robot.priceType !== "not-published") return false;
      if (category && !robot.category.toLowerCase().includes(category)) return false;
      if (!matchesSize(robot, size)) return false;
      if (year && robot.releaseYear !== Number(year)) return false;
      return true;
    });

    return [...rows].sort((a, b) => {
      if (sort === "price-asc" || sort === "price-desc") {
        const aPrice = getNumericPrice(a);
        const bPrice = getNumericPrice(b);
        if (aPrice === null && bPrice === null) return a.name.localeCompare(b.name);
        if (aPrice === null) return 1;
        if (bPrice === null) return -1;
        return sort === "price-asc" ? aPrice - bPrice : bPrice - aPrice;
      }
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "manufacturer") return a.manufacturerName.localeCompare(b.manufacturerName);
      if (sort === "country") return a.robotOriginCountry.localeCompare(b.robotOriginCountry);
      if (sort === "year") return b.releaseYear - a.releaseYear;
      if (sort === "verified") return b.lastVerifiedAt.localeCompare(a.lastVerifiedAt);
      if (sort === "available") return Number(b.availability === "Available") - Number(a.availability === "Available");
      if (sort === "rating") return Number(communityStats[b.id]?.rankingScore || 0) - Number(communityStats[a.id]?.rankingScore || 0);
      if (sort === "reviews") return Number(communityStats[b.id]?.reviewCount || 0) - Number(communityStats[a.id]?.reviewCount || 0);
      return Number(b.featured) - Number(a.featured) || a.name.localeCompare(b.name);
    });
  }, [availability, category, communityStats, country, manufacturer, price, query, size, sort, year]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const statsRobotIds = (sort === "rating" || sort === "reviews" ? humanoidRobots : visible).map((robot) => robot.id);
  const statsRobotKey = statsRobotIds.join("|");
  useEffect(() => {
    let active = true;
    if (typeof window !== "undefined" && window.__TECHNIA_DISABLE_REMOTE_DATA__) {
      return () => { active = false; };
    }
    import("../../lib/firestoreCommunity")
      .then(async ({ getRobotStatsBatch }) => {
        const ids = statsRobotKey.split("|").filter(Boolean);
        const batches = Array.from({ length: Math.ceil(ids.length / 30) }, (_, index) => ids.slice(index * 30, (index + 1) * 30));
        const results = await Promise.all(batches.map((batch) => getRobotStatsBatch(batch)));
        return Object.assign({}, ...results);
      })
      .then((stats) => { if (active) setCommunityStats((current) => ({ ...current, ...stats })); })
      .catch(() => {});
    return () => { active = false; };
  }, [statsRobotKey]);
  const categories = [...new Set(humanoidRobots.map((robot) => robot.category))].sort();
  const years = [...new Set(humanoidRobots.map((robot) => robot.releaseYear).filter(Number.isFinite))].sort((a, b) => b - a);
  const availabilityOptions = [...new Set(humanoidRobots.map((robot) => robot.availability))].sort();
  const breadcrumbs = [{ label: "Home", to: "/" }, { label: "Humanoid robots", to: "/humanoid-robots" }];
  const schema = buildCatalogCollectionSchema(
    "/humanoid-robots",
    humanoidRobots,
    "Humanoid Robot Catalog and Prices",
    "Compare recorded humanoid robots by manufacturer, country, availability and publicly displayed price.",
  );

  return (
    <>
      <SEO
        title="Humanoid Robot Catalog, Specifications and Prices"
        description="Browse humanoid specifications, price plans and source dates. Filter recorded models by manufacturer, country, availability and use case."
        path="/humanoid-robots"
        schema={[schema, buildBreadcrumbSchema(breadcrumbs)]}
      />
      <Breadcrumbs items={breadcrumbs} />
      <div>
        <section className="bg-neutral-950 py-14 text-white sm:py-20">
          <div className="site-shell">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-blue-300">Source-linked catalog · latest record review {humanoidCatalogStats.lastVerifiedAt}</p>
            <h1 className="mt-4 max-w-4xl text-4xl font-black tracking-tight sm:text-6xl">Humanoid Robot Catalog and Specifications</h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-neutral-300">Compare the models recorded in this catalog by manufacturer, country, specifications and availability. Community reviews appear after moderation when contributions are available.</p>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-neutral-400">Source dates and review scope vary by model. Price sorting uses USD purchase offers and excludes deposits and recurring plans.</p>
            <div className="mt-8"><CatalogStats stats={humanoidCatalogStats} /></div>
            <div className="mt-8 flex flex-col gap-3 rounded-2xl border border-neutral-700 bg-neutral-900 p-3 sm:flex-row">
              <input aria-label="Search humanoid robots" value={query} onChange={(event) => update("q", event.target.value)} className="min-h-12 flex-1 rounded-xl border border-neutral-700 bg-white px-4 text-neutral-950" placeholder="Search humanoid robots, companies or countries" />
              <button type="button" className="btn-primary whitespace-nowrap" onClick={() => setSuggestOpen(true)}>Add a Humanoid Robot</button>
            </div>
          </div>
        </section>

        <TopRatedRobots />

        <section className="site-shell pt-10">
          <Link to="/robotics/humanoid-robot-data" className="group block rounded-3xl border border-blue-200 bg-blue-50 p-6 transition hover:border-blue-400 sm:p-8">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Humanoid data library</p>
            <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
              <div><h2 className="text-2xl font-black text-neutral-950 sm:text-3xl">Explore 20 source-linked charts</h2><p className="mt-2 max-w-3xl leading-7 text-neutral-700">Prices, dimensions, availability, use cases, hardware, deployment, reliability, funding and training-data architecture.</p></div>
              <span className="font-black text-blue-700 group-hover:underline">Open humanoid data →</span>
            </div>
          </Link>
          <Link to="/robotics-news/world-humanoid-robot-games-2026" className="group mt-5 block rounded-3xl border border-neutral-200 bg-white p-6 transition hover:border-blue-300 sm:p-8">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">Humanoid competition · August 22–26</p>
            <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
              <div><h2 className="text-2xl font-black text-neutral-950 sm:text-3xl">World Humanoid Robot Games 2026</h2><p className="mt-2 max-w-3xl leading-7 text-neutral-700">See the Beijing schedule, 2,056-robot field, 666 teams, sports, scenario tasks and confirmed daily program status.</p></div>
              <span className="font-black text-blue-700 group-hover:underline">View schedule and events →</span>
            </div>
          </Link>
        </section>

        <section className="site-shell py-10">
          <div className="grid gap-8 lg:grid-cols-[300px_minmax(0,1fr)]">
            <aside className="h-fit rounded-3xl border border-neutral-200 bg-white p-5 shadow-sm lg:sticky lg:top-24" aria-label="Catalog filters">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-black">Filters</h2>
                <button type="button" className="text-sm font-bold text-blue-700 hover:underline" onClick={() => setParams({})}>Reset</button>
              </div>
              <div className="mt-5 space-y-4">
                <label className="block text-sm font-bold">Search
                  <input aria-label="Search robots" value={query} onChange={(event) => update("q", event.target.value)} className="mt-2 w-full rounded-xl border border-neutral-300 px-3 py-2.5" placeholder="Robot or manufacturer" />
                </label>
                <label className="block text-sm font-bold">Manufacturer
                  <select value={manufacturer} onChange={(event) => update("manufacturer", event.target.value)} className="mt-2 w-full rounded-xl border border-neutral-300 px-3 py-2.5">
                    <option value="">All manufacturers</option>
                    {humanoidManufacturers.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </label>
                <label className="block text-sm font-bold">Country of origin
                  <select value={country} onChange={(event) => update("country", event.target.value)} className="mt-2 w-full rounded-xl border border-neutral-300 px-3 py-2.5">
                    <option value="">All countries</option>
                    {humanoidCountries.map((item) => <option key={item.code} value={item.code}>{item.flag} {item.name}</option>)}
                  </select>
                </label>
                <label className="block text-sm font-bold">Availability
                  <select value={availability} onChange={(event) => update("availability", event.target.value)} className="mt-2 w-full rounded-xl border border-neutral-300 px-3 py-2.5">
                    <option value="">All statuses</option>
                    {availabilityOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </label>
                <label className="block text-sm font-bold">Price status
                  <select value={price} onChange={(event) => update("price", event.target.value)} className="mt-2 w-full rounded-xl border border-neutral-300 px-3 py-2.5">
                    <option value="">All price statuses</option>
                    <option value="public">Public price</option>
                    <option value="quote">Price on request</option>
                    <option value="unpublished">Not publicly sold</option>
                    <option value="unverified">Price not verified</option>
                  </select>
                </label>
                <label className="block text-sm font-bold">Category
                  <select value={category} onChange={(event) => update("category", event.target.value)} className="mt-2 w-full rounded-xl border border-neutral-300 px-3 py-2.5">
                    <option value="">All categories</option>
                    {categories.map((item) => <option key={item} value={item.toLowerCase()}>{item}</option>)}
                  </select>
                </label>
                <label className="block text-sm font-bold">Height
                  <select value={size} onChange={(event) => update("size", event.target.value)} className="mt-2 w-full rounded-xl border border-neutral-300 px-3 py-2.5">
                    <option value="">All sizes</option><option value="compact">Under 120 cm</option><option value="medium">120–164 cm</option><option value="full">165 cm and taller</option>
                  </select>
                </label>
                <label className="block text-sm font-bold">Release year
                  <select value={year} onChange={(event) => update("year", event.target.value)} className="mt-2 w-full rounded-xl border border-neutral-300 px-3 py-2.5">
                    <option value="">All years</option>{years.map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </label>
              </div>
            </aside>

            <div>
              <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-neutral-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-bold text-neutral-700">{filtered.length} robot{filtered.length === 1 ? "" : "s"} found</p>
                <label className="flex items-center gap-3 text-sm font-bold">Sort
                  <select value={sort} onChange={(event) => update("sort", event.target.value)} className="rounded-xl border border-neutral-300 px-3 py-2">
                    <option value="featured">Featured</option><option value="rating">Highest Rated</option><option value="reviews">Most Reviewed</option><option value="price-asc">USD purchase price low to high</option><option value="price-desc">USD purchase price high to low</option><option value="name">Name</option><option value="manufacturer">Manufacturer</option><option value="country">Country</option><option value="year">Newest year</option><option value="verified">Last verified</option><option value="available">Available first</option>
                  </select>
                </label>
              </div>
              {visible.length ? (
                <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">{visible.map((robot) => <HumanoidRobotCard key={robot.id} robot={robot} communityStats={communityStats[robot.id]} />)}</div>
              ) : (
                <div className="rounded-3xl border border-dashed border-neutral-300 bg-neutral-50 p-12 text-center"><h2 className="text-2xl font-black">No robots match these filters</h2><button className="btn-primary mt-5" onClick={() => setParams({})}>Reset filters</button></div>
              )}
              {pageCount > 1 && <nav className="mt-8 flex flex-wrap justify-center gap-2" aria-label="Catalog pagination">{Array.from({ length: pageCount }, (_, index) => index + 1).map((number) => <button key={number} className={`h-10 min-w-10 rounded-xl border px-3 font-bold ${number === safePage ? "border-neutral-950 bg-neutral-950 text-white" : "border-neutral-300 bg-white"}`} onClick={() => update("page", String(number))}>{number}</button>)}</nav>}
            </div>
          </div>
        </section>

        <section className="border-y border-neutral-200 bg-neutral-50 py-12">
          <div className="site-shell grid gap-6 md:grid-cols-2">
            <Link to="/humanoid-robots/manufacturers" className="soft-card soft-card-hover p-7"><p className="text-sm font-black uppercase tracking-[0.18em] text-blue-700">Browse the industry</p><h2 className="mt-3 text-3xl font-black">Manufacturers</h2><p className="mt-3 leading-7 text-neutral-600">Compare {humanoidCatalogStats.manufacturerCount} listed developers and every model included for each company.</p></Link>
            <Link to="/humanoid-robots/countries" className="soft-card soft-card-hover p-7"><p className="text-sm font-black uppercase tracking-[0.18em] text-blue-700">Origin, not deployment</p><h2 className="mt-3 text-3xl font-black">Countries</h2><p className="mt-3 leading-7 text-neutral-600">Browse robots by the country of the original manufacturer, across {humanoidCatalogStats.countryCount} countries.</p></Link>
          </div>
        </section>
      </div>
      <RobotSuggestionModal open={suggestOpen} onClose={() => setSuggestOpen(false)} />
    </>
  );
}
